import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { useCatalogues } from "@/features/boutique/hooks/useCatalogues";
import { REFS_SEARCH_PATH } from "@/features/boutique/routes";
import { SearchBar, SearchBarSection } from "@/features/shop";
import { CmsSearchBarSection } from "@/features/shop/cms/components/CmsSearchBarSection";
import type { SearchBarAnimationConfig } from "@/features/shop/cms/schemas/searchBarPayload.schema";
import { useShopPageLoadingState } from "@/features/shop/hooks/useShopPageLoadingState";
import { SearchBarContainerProps } from "@/features/shop/types/searchBar.types";

const DEFAULT_PLACEHOLDER = "Disneyland Paris, Amazon, Fnac, Netflix, Parc Astérix, Camping ...";
const DEFAULT_CATALOGUE_ID = 1;

export const SearchBarContainer = ({
  variant = "hero",
  title,
  placeholder,
  sectionOverride,
  disableCmsWrapper = false,
  searchValue,
  onSearchChange,
  debounceDelay = 300,
  onSubmit,
  catalogueId,
  onCatalogueChange,
}: SearchBarContainerProps = {}) => {
  const navigate = useNavigate();
  const shouldUseCatalogueSelector = variant === "compact";
  const { catalogues, isLoading: isCatalogueLoading } = useCatalogues({ enabled: shouldUseCatalogueSelector });
  const [query, setQuery] = useState(searchValue ?? "");
  const isCataloguePropProvided = catalogueId !== undefined;
  const [internalCatalogueId, setInternalCatalogueId] = useState<number | null>(catalogueId ?? null);
  const isControlledSearch = typeof onSearchChange === "function";
  const effectivePlaceholder = placeholder ?? sectionOverride?.placeholder ?? DEFAULT_PLACEHOLDER;
  const selectedCatalogueId = shouldUseCatalogueSelector
    ? (isCataloguePropProvided ? catalogueId ?? null : internalCatalogueId ?? catalogues[0]?.id ?? DEFAULT_CATALOGUE_ID)
    : undefined;

  const { shopIndexPage } = useShopPageLoadingState({ enabled: !disableCmsWrapper });
  const onSearchChangeRef = useRef(onSearchChange);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  onSearchChangeRef.current = onSearchChange;

  useEffect(() => {
    if (!isControlledSearch) return;
    setQuery(searchValue ?? "");
  }, [isControlledSearch, searchValue]);

  const handleInputChange = (value: string) => {
    setQuery(value);

    if (isControlledSearch) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        onSearchChangeRef.current?.(value);
      }, debounceDelay);
    }
  };

  const handleCatalogueChangeInternal = (newCatalogueId: number) => {
    if (!isCataloguePropProvided) {
      setInternalCatalogueId(newCatalogueId);
    }
    onCatalogueChange?.(newCatalogueId);
  };

  const searchBarContent = (
    <SearchBar
      value={query}
      onChange={handleInputChange}
      onFocus={() => {}}
      onBlur={() => {}}
      results={[]}
      isDropdownOpen={false}
      onSelectResult={() => {}}
      placeholder=""
    />
  );

  const handleClear = () => {
    setQuery("");
    if (isControlledSearch) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      onSearchChangeRef.current?.("");
    }
  };

  const handleSubmit = (value?: string) => {
    const normalizedQuery = (value ?? query).trim();
    if (!normalizedQuery) return;

    if (isControlledSearch) {
      onSearchChange?.(normalizedQuery);
    }
    else {
      setQuery(normalizedQuery);
    }

    if (onSubmit) {
      onSubmit(normalizedQuery, selectedCatalogueId);
      return;
    }

    const params = new URLSearchParams();
    params.set("q", normalizedQuery);
    if (shouldUseCatalogueSelector && selectedCatalogueId) {
      params.set("catalogue", selectedCatalogueId.toString());
    }

    navigate(`${REFS_SEARCH_PATH}?${params.toString()}`);
  };

  const renderSearchBarSection = (
    sectionTitle?: string,
    animationConfig?: SearchBarAnimationConfig,
    gradientColors?: string[],
    yeatiImageUrl?: string | null,
    sectionVariant = variant,
  ) => {
    const sectionCatalogueOptions = shouldUseCatalogueSelector
      ? catalogues.map(({ id, name }) => ({ id, name }))
      : undefined;

    return (
      <SearchBarSection
        variant={sectionVariant}
        title={sectionTitle}
        animationConfig={animationConfig}
        gradientColors={gradientColors}
        yeatiImageUrl={yeatiImageUrl}
        catalogueOptions={sectionCatalogueOptions}
        selectedCatalogueId={shouldUseCatalogueSelector ? selectedCatalogueId ?? null : undefined}
        onCatalogueChange={shouldUseCatalogueSelector ? handleCatalogueChangeInternal : undefined}
        isCatalogueLoading={shouldUseCatalogueSelector ? isCatalogueLoading : false}
      >
        <SearchBar
          value={query}
          onChange={handleInputChange}
          onFocus={() => {}}
          onBlur={() => {}}
          results={[]}
          isDropdownOpen={false}
          onSelectResult={() => {}}
          showHighlight={sectionVariant === "hero"}
          placeholder={effectivePlaceholder}
          showClearButton={sectionVariant !== "hero" || isControlledSearch}
          onClear={handleClear}
          onSubmit={handleSubmit}
        />
      </SearchBarSection>
    );
  };

  if (!disableCmsWrapper && shopIndexPage) {
    return (
      <CmsSearchBarSection pageCode={shopIndexPage.code}>
        {searchBarContent}
      </CmsSearchBarSection>
    );
  }

  return (
    renderSearchBarSection(
      sectionOverride?.title ?? title,
      sectionOverride?.animationConfig,
      sectionOverride?.gradientColors,
      sectionOverride?.yeatiImageUrl,
      sectionOverride?.variant ?? variant,
    )
  );
};
